import { InvoiceDraft } from '../../types';
import { invoiceTotals } from '../invoice';

function escapeCsvCell(value: string | number): string {
  const str = String(value);
  return `"${str.replace(/"/g, '""')}"`;
}

function formatMoney(amount: number): string {
  return (Number(amount) || 0).toFixed(2);
}

function customerName(invoice: InvoiceDraft): string {
  const name = (invoice.toName || '').trim();
  const company = (invoice.toCompany || '').trim();
  if (name && company && name !== company) return `${name} — ${company}`;
  return name || company || '';
}

/** e.g. Invoices Aug-11.csv */
export function invoicesCsvFilename(date: Date = new Date()): string {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const day = String(date.getDate()).padStart(2, '0');
  return `Invoices ${months[date.getMonth()]}-${day}.csv`;
}

/** One row per line item; invoice-level fields repeat on each row. */
export function exportInvoicesToCSV(invoices: InvoiceDraft[]): string {
  const headers = [
    'Customer',
    'Invoice Number',
    'Date',
    'Due Date',
    'Payment Terms',
    'Total',
    'Amount Paid',
    'Ship To',
    'Notes',
    'Item',
    'Description',
    'Quantity',
    'Unit Cost',
    'Discount',
    'Tax',
    'Shipping',
  ];

  const rows: string[][] = [];

  for (const inv of invoices) {
    const totals = invoiceTotals(inv);
    const lines =
      inv.items.length > 0
        ? inv.items
        : [
            {
              description: '',
              details: '',
              qty: 0,
              unit: '',
              unitPrice: 0,
              vatPercent: 0,
            },
          ];

    for (const item of lines) {
      rows.push([
        customerName(inv),
        inv.number || '',
        inv.date || '',
        inv.dueDate || '',
        inv.paymentTerms || '',
        formatMoney(totals.total),
        formatMoney(totals.amountPaid),
        inv.shipTo || '',
        inv.notes || '',
        item.description || '',
        item.details || '',
        String(item.qty ?? 0),
        formatMoney(item.unitPrice ?? 0),
        formatMoney(totals.discount),
        formatMoney(totals.tax),
        formatMoney(totals.shipping),
      ]);
    }
  }

  return [headers.join(','), ...rows.map((row) => row.map(escapeCsvCell).join(','))].join('\n');
}

export function downloadCSV(content: string, filename: string): void {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
}
